"use client";

import { usePathname } from "next/navigation";

import { useState } from "react";
import {
  Dialog,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { IoCarSportOutline, IoAirplaneOutline } from "react-icons/io5";
import { LiaIndustrySolid } from "react-icons/lia";
import { Fragment } from "react";

import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";

import EncoreLogoImg from "@/public/imgs/Encore-Logo.jpg";
import AuthorizedSystemIntegratorImg from "@/public/imgs/AuthorizedSystemIntegrator.png";

const products = [
  {
    name: "Automotive",
    description: "Learn about our automotive systems and offerings.",
    href: "/systems/automotive",
    icon: IoCarSportOutline,
  },
  {
    name: "Aerospace",
    description: "Explore advanced aerospace technologies and services.",
    href: "/systems/aerospace",
    icon: IoAirplaneOutline,
  },
  {
    name: "General Industry",
    description: "Discover solutions for diverse industrial applications.",
    href: "/systems/general-industry",
    icon: LiaIndustrySolid,
  },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const isSystemsActive = pathname.startsWith("/systems");

  return (
    <>
      <header
        className={`sticky top-5 z-40 bg-gray-200/80 backdrop-blur-lg  mx-8 my-2 p-5  md:p-3 rounded-xl shadow-lg transition-all duration-300 ${
          mobileMenuOpen ? "hidden lg:flex" : ""
        }`}
        // data-aos="fade-down"
        // className="bg-white bg-opacity-90 md:bg-opacity-50 relative z-20 px-10"
      >
        <nav
          aria-label="Global"
          className="flex items-center justify-between p-2 lg:px-8"
        >
          <div className="flex lg:flex-1">
            <a href="/" className="-m-1.5 p-1.5">
              <span className="sr-only">Your Company</span>
              <Image
                src={EncoreLogoImg}
                height={170}
                width={100}
                alt="Encore Automation Logo"
                className="w-[4rem] md:w-[6rem]"
              />
            </a>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <PopoverGroup className="hidden lg:flex lg:gap-x-12 lg:items-center z-50">
            <Popover className="relative">
              {({ open, close }) => (
                <>
                  <PopoverButton
                    className={`flex items-center gap-x-1 text-lg font-light hover:text-indigo-600 ${
                      isSystemsActive ? "text-indigo-600" : "text-gray-900"
                    }`}
                  >
                    Systems
                    <ChevronDownIcon
                      aria-hidden="true"
                      className={`size-5 flex-none text-gray-500 transition-transform duration-200 ${
                        open ? "rotate-180" : ""
                      }`}
                    />
                  </PopoverButton>

                  {/* 👇 Overlay only shows when open */}
                  {open && (
                    <div
                      className="fixed inset-0 z-40 m bg-transparent h-screen w-full"
                      onClick={() => close()}
                    />
                  )}

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <PopoverPanel className="absolute top-full -left-8 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 z-50">
                      <div className="p-4">
                        {products.map((item) => (
                          <div
                            key={item.name}
                            className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm/6 hover:bg-gray-50"
                          >
                            <div className="flex size-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                              <item.icon
                                aria-hidden="true"
                                className="size-6 text-gray-600 group-hover:text-indigo-600"
                              />
                            </div>
                            <div className="flex-auto">
                              <Link
                                href={item.href}
                                onClick={() => close()} // ✅ closes on click
                                className="block font-semibold text-gray-900"
                              >
                                {item.name}
                                <span className="absolute inset-0" />
                              </Link>
                              <p className="mt-1 text-gray-600">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </PopoverPanel>
                  </Transition>
                </>
              )}
            </Popover>

            <Link
              href="/about"
              className={`text-lg font-light hover:text-indigo-600 ${
                pathname === "/about" ? "text-indigo-600" : "text-gray-900"
              }`}
            >
              Company
            </Link>
            <Link
              href="/support"
              className={`text-lg font-light hover:text-indigo-600 ${
                pathname === "/support" ? "text-indigo-600" : "text-gray-900"
              }`}
            >
              Support
            </Link>
            <Link
              href="/careers"
              className={`text-lg font-light hover:text-indigo-600 ${
                pathname === "/careers" ? "text-indigo-600" : "text-gray-900"
              }`}
            >
              Careers
            </Link>
            <Link
              href="/contact-us"
              className={`text-lg font-light hover:text-indigo-600 ${
                pathname === "/contact-us" ? "text-indigo-600" : "text-gray-900"
              }`}
            >
              Contact Us
            </Link>
            <Link
              href="/employee-portal"
              className={`text-lg font-light hover:text-indigo-600 ${
                pathname === "/employee-portal"
                  ? "text-indigo-600"
                  : "text-gray-900"
              }`}
            >
              Employee Portal
            </Link>

            <Image
              alt="FANUC Authorized System Integration"
              src={AuthorizedSystemIntegratorImg}
              width={100}
              height={100}
            />
          </PopoverGroup>
        </nav>
        <AnimatePresence>
          {mobileMenuOpen && (
            <Dialog
              open={mobileMenuOpen}
              onClose={setMobileMenuOpen}
              className="lg:hidden"
            >
              <div className="fixed inset-0 z-10" />
              <motion.div
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100%", opacity: 0 }}
                transition={{ type: "tween", duration: 0.3 }}
                className="fixed top-0 right-0 z-20 w-full sm:max-w-sm h-full overflow-y-auto bg-white text-white px-6 py-6 shadow-xl"
              >
                {" "}
                <div className="flex items-center justify-between">
                  <a href="#" className="-m-1.5 p-1.5">
                    <span className="sr-only">Your Company</span>
                    <Image
                      src={EncoreLogoImg}
                      height={150}
                      width={80}
                      alt="Encore Automation Logo"
                    />
                  </a>
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    className="-m-2.5 rounded-md p-2.5 text-gray-700"
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon aria-hidden="true" className="size-6" />
                  </button>
                </div>
                <div className="mt-6 flow-root">
                  <div className="-my-6 divide-y divide-gray-500/10">
                    <div className="space-y-2 py-6">
                      <Disclosure as="div" className="-mx-3">
                        <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pr-3.5 pl-3 text-base/7 font-semibold text-gray-900 hover:bg-gray-50">
                          Systems
                          <ChevronDownIcon
                            aria-hidden="true"
                            className="size-5 flex-none group-data-open:rotate-180"
                          />
                        </DisclosureButton>
                        <DisclosurePanel className="mt-2 space-y-2">
                          {/* {[...products, ...callsToAction].map((item) => ( */}
                          {[...products].map((item) => (
                            <DisclosureButton
                              key={item.name}
                              as="a"
                              href={item.href}
                              className="block rounded-lg py-2 pr-3 pl-6 text-sm/7 font-semibold text-gray-900 hover:bg-gray-50"
                            >
                              {item.name}
                            </DisclosureButton>
                          ))}
                        </DisclosurePanel>
                      </Disclosure>
                      <Link
                        href="/about"
                        onClick={() => setMobileMenuOpen(false)}
                        className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                      >
                        Company
                      </Link>
                      <Link
                        href="/support"
                        onClick={() => setMobileMenuOpen(false)}
                        className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                      >
                        Support
                      </Link>
                      <Link
                        href="/careers"
                        onClick={() => setMobileMenuOpen(false)}
                        className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                      >
                        Careers
                      </Link>
                      <Link
                        href="/contact-us"
                        onClick={() => setMobileMenuOpen(false)}
                        className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                      >
                        Contact Us
                      </Link>
                      <Link
                        href="/employee-portal"
                        onClick={() => setMobileMenuOpen(false)}
                        className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                      >
                        Employee Portal
                      </Link>
                      <Image
                        alt="FANUC Authorized System Integration"
                        src={AuthorizedSystemIntegratorImg}
                        width={100}
                        height={100}
                        className=" "
                      />
                    </div>
                    {/* <div className="py-6">
                <a
                  href="#"
                  className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                >
                  Log in
                </a>
              </div> */}
                  </div>
                </div>
              </motion.div>
            </Dialog>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
